import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    technical: true, // Always enabled, cannot be disabled
    analytics: false,
    personalization: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      technical: true,
      analytics: true,
      personalization: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
    // Here you would enable analytics scripts like Google Analytics
    enableAnalytics();
  };

  const handleRejectAll = () => {
    const consentData = {
      technical: true,
      analytics: false,
      personalization: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
    setShowSettings(false);
    
    if (preferences.analytics) {
      enableAnalytics();
    }
  };

  const enableAnalytics = () => {
    // Placeholder for enabling Google Analytics or similar
    // In production, you would load the GA script here
    console.log('Analytics cookies enabled');
  };

  // Function to open cookie settings (can be called from footer)
  useEffect(() => {
    window.openCookieSettings = () => {
      setShowBanner(true);
      setShowSettings(true);
    };
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        onClick={() => {}} // Prevent closing on backdrop click
      />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            
            {!showSettings ? (
              // Main Banner View
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#e4002b]/20 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-[#e4002b]" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">
                      Usamos cookies
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed mb-4">
                      Utilizamos cookies propias y de terceros para analizar nuestros servicios y mostrarte publicidad relacionada con tus preferencias. 
                      Puedes aceptar todas las cookies, rechazarlas o configurar tus preferencias.{' '}
                      <Link to="/politica-cookies" className="text-[#e4002b] hover:underline">
                        Más información
                      </Link>
                    </p>
                    
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="px-6 py-2.5 bg-[#e4002b] hover:bg-[#c70024] text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Aceptar todas
                      </button>
                      
                      <button
                        onClick={handleRejectAll}
                        className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors border border-white/20"
                      >
                        Rechazar
                      </button>
                      
                      <button
                        onClick={() => setShowSettings(true)}
                        className="px-6 py-2.5 bg-transparent hover:bg-white/10 text-white/70 hover:text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Configurar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Settings View
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-white flex items-center gap-3">
                    <Settings className="w-5 h-5 text-[#e4002b]" />
                    Configuración de Cookies
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white/60" />
                  </button>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Technical Cookies - Always enabled */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-1">Cookies técnicas</h4>
                        <p className="text-white/50 text-sm">
                          Necesarias para el funcionamiento básico del sitio web. No pueden ser desactivadas.
                        </p>
                      </div>
                      <div className="ml-4">
                        <div className="w-12 h-6 bg-[#e4002b] rounded-full flex items-center px-1 cursor-not-allowed opacity-70">
                          <div className="w-4 h-4 bg-white rounded-full ml-auto" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Analytics Cookies */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-1">Cookies de análisis</h4>
                        <p className="text-white/50 text-sm">
                          Nos permiten medir y analizar el uso del sitio web para mejorar nuestros servicios.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                          className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                            preferences.analytics ? 'bg-[#e4002b]' : 'bg-white/20'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Personalization Cookies */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-1">Cookies de personalización</h4>
                        <p className="text-white/50 text-sm">
                          Permiten recordar tus preferencias para ofrecerte una experiencia personalizada.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => setPreferences(p => ({ ...p, personalization: !p.personalization }))}
                          className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                            preferences.personalization ? 'bg-[#e4002b]' : 'bg-white/20'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.personalization ? 'translate-x-6' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-2.5 bg-[#e4002b] hover:bg-[#c70024] text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Guardar preferencias
                  </button>
                  
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors border border-white/20"
                  >
                    Aceptar todas
                  </button>
                </div>
                
                <p className="text-white/40 text-xs mt-4">
                  Para más información, consulta nuestra{' '}
                  <Link to="/politica-cookies" className="text-[#e4002b] hover:underline">
                    Política de Cookies
                  </Link>.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
